import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ClipboardDocumentListIcon, CheckIcon } from '@heroicons/react/24/outline';

const focusSelector = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const sectionTitleCls = 'text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 break-words';
const detailLabelCls = 'break-words text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400';
const detailValueCls = 'mt-0.5 whitespace-pre-wrap break-words';

const formatCodeDisplay = (value) => {
  if (!value) return '—';
  const text = String(value);
  const trimmed = text.trim();
  if (!trimmed) return '—';
  if (/^code\s*:/i.test(trimmed)) return text;
  if (/\s+\S+/.test(trimmed)) return text;
  return `Code: ${text}`;
};

const humanizeKey = (key = '') =>
  key
    .toString()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (ch) => ch.toUpperCase());

const renderDetailValue = (value) => {
  if (value == null) return '—';

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="rounded-lg bg-white/90 px-3 py-2 shadow-inner">
            {typeof item === 'object' && item !== null ? (
              <dl className="space-y-1 text-[12px] text-slate-600">
                {Object.entries(item).map(([childKey, childVal]) => (
                  <div key={childKey}>
                    <dt className={detailLabelCls}>{humanizeKey(childKey)}</dt>
                    <dd className={detailValueCls}>{renderDetailValue(childVal)}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-[12px] text-slate-600 whitespace-pre-wrap break-words">{String(item)}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return '—';
    return (
      <dl className="space-y-1 text-[12px] text-slate-600">
        {entries.map(([childKey, childVal]) => (
          <div key={childKey}>
            <dt className={detailLabelCls}>{humanizeKey(childKey)}</dt>
            <dd className={detailValueCls}>{renderDetailValue(childVal)}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return String(value);
};

function formatDateRange(primary) {
  const options = { month: 'short', day: 'numeric', year: 'numeric' };
  try {
    const { startDate, endDate } = primary || {};
    if (!startDate && !endDate) return null;
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (start && end) {
      return `${start.toLocaleDateString(undefined, options)} – ${end.toLocaleDateString(undefined, options)}`;
    }
    if (start) return start.toLocaleDateString(undefined, options);
    if (end) return end.toLocaleDateString(undefined, options);
  } catch (_) {
    return null;
  }
  return null;
}

function LockboxEmptyState({ label }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/60 p-4 text-sm text-slate-500 shadow-inner">
      No {label} available for this order.
    </div>
  );
}

export default function LockboxDetailsSheet({
  open,
  onClose,
  data,
  propertyLabel,
  onCopyCode,
  copyToast,
}) {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const [touchOffset, setTouchOffset] = useState(0);
  const startYRef = useRef(null);
  const velocityRef = useRef(0);

  const lockBox = data?.lockBox || {};
  const propertyFields = Array.isArray(data?.propertyAdditionalFields)
    ? data.propertyAdditionalFields
    : [];
  const reservationInfo = data?.reservationAdditionalFields || {};
  const primaryReservation = reservationInfo?.primary;
  const neighborGroups = useMemo(() => {
    const neighbors = reservationInfo?.neighbors;
    if (!neighbors) return [];

    if (Array.isArray(neighbors)) {
      return neighbors.length
        ? [{ key: 'nearby', title: 'Nearby Reservations', reservations: neighbors }]
        : [];
    }

    if (typeof neighbors === 'object') {
      return Object.entries(neighbors)
        .filter(([, value]) => Array.isArray(value) && value.length > 0)
        .map(([key, value]) => ({ key, title: humanizeKey(key), reservations: value }));
    }

    return [];
  }, [reservationInfo?.neighbors]);
  const notes = Array.isArray(data?.notes) ? data.notes : data?.notes ? [data.notes] : [];

  useEffect(() => {
    if (!open) return undefined;

    lastFocusedRef.current = document.activeElement;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = panelRef.current?.querySelectorAll(focusSelector);
      if (!focusables || focusables.length === 0) {
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      }
    };

    const focusables = panelRef.current?.querySelectorAll(focusSelector);
    if (focusables && focusables.length) {
      focusables[0].focus();
    } else {
      panelRef.current?.focus();
    }

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (lastFocusedRef.current && typeof lastFocusedRef.current.focus === 'function') {
        lastFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setTouchOffset(0);
      startYRef.current = null;
    }
  }, [open]);

  const handleTouchStart = (event) => {
    if (!panelRef.current) return;
    startYRef.current = event.touches[0]?.clientY ?? null;
    velocityRef.current = 0;
  };

  const handleTouchMove = (event) => {
    if (startYRef.current == null) return;
    const currentY = event.touches[0]?.clientY ?? 0;
    const delta = currentY - startYRef.current;
    if (delta < 0) {
      setTouchOffset(0);
      return;
    }
    const clamped = Math.min(delta, 180);
    velocityRef.current = clamped - touchOffset;
    setTouchOffset(clamped);
  };

  const handleTouchEnd = () => {
    const shouldClose = touchOffset > 120 || velocityRef.current > 18;
    setTouchOffset(0);
    startYRef.current = null;
    velocityRef.current = 0;
    if (shouldClose) {
      onClose();
    }
  };

  const dateRange = useMemo(() => (open ? formatDateRange(primaryReservation) : null), [open, primaryReservation]);

  if (!open) return null;

  const headline = propertyLabel || data?.propertyName || data?.propertyId || 'Lockbox Details';
  const copyAvailable = Boolean(lockBox?.code);

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/30 backdrop-blur-md"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={headline}
        tabIndex={-1}
        className="relative flex h-full w-full max-w-3xl flex-col overflow-hidden border border-white/20 bg-white/90 shadow-[0_28px_80px_rgba(15,23,42,0.35)] backdrop-blur-xl transition-transform duration-150 ease-out md:h-auto md:max-h-[90vh] md:rounded-3xl"
        style={{ transform: `translateY(${touchOffset}px)` }}
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <header className="flex items-center justify-between border-b border-slate-200/70 bg-white/60 px-6 py-5 backdrop-blur-md">
          <div className="min-w-0 flex-1 pr-4">
            <p className="whitespace-normal break-words text-[15px] font-semibold leading-snug text-slate-900">
              {headline}
            </p>
            {data?.propertyId && (
              <p className="mt-0.5 whitespace-normal break-words text-xs leading-relaxed text-slate-500">Property ID: {data.propertyId}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/80 bg-white/70 text-slate-600 transition hover:border-slate-300 hover:bg-white/90 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </button>
        </header>

        <div className="relative flex-1 overflow-y-auto bg-gradient-to-br from-white/95 to-slate-50 px-6 py-6 md:px-10 md:py-8">
          {copyToast && (
            <div className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2">
              <div className="flex items-center gap-2 rounded-full bg-slate-900/85 px-4 py-2 text-xs font-medium text-white shadow-lg">
                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                Copied to clipboard
              </div>
            </div>
          )}

          {notes.map((note, index) => (
            <div
              key={`note-${index}`}
              className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-100/65 px-4 py-3 text-[13px] text-amber-900 shadow-sm"
            >
              <div className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
              <p className="whitespace-pre-wrap break-words leading-relaxed">{note}</p>
            </div>
          ))}

          <section className="grid gap-6 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
            <div className="flex flex-col gap-3 rounded-3xl border border-white/30 bg-white/70 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
              <div className="flex items-center justify-between gap-3">
                <span className={sectionTitleCls}>Lockbox Code</span>
                {copyAvailable && (
                  <button
                    type="button"
                    onClick={() => onCopyCode(lockBox.code)}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/70 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />
                    Copy
                  </button>
                )}
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900">
                {formatCodeDisplay(lockBox?.code)}
              </p>
              {lockBox?.updatedAt && (
                <p className="text-xs text-slate-500">Updated {new Date(lockBox.updatedAt).toLocaleString()}</p>
              )}
            </div>

            <div className="flex min-h-[180px] flex-col rounded-3xl border border-white/40 bg-white/70 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]">
              <div className="mb-2 flex items-center justify-between">
                <span className={sectionTitleCls}>Directions</span>
                {lockBox?.lastEditedBy && (
                  <span className="min-w-0 break-words text-xs text-slate-400">Edited by {lockBox.lastEditedBy}</span>
                )}
              </div>
              <div className="custom-scrollbar flex-1 overflow-y-auto rounded-2xl bg-white/80 p-4 text-[13px] leading-relaxed text-slate-700 shadow-inner">
                {lockBox?.directions ? (
                  <p className="whitespace-pre-wrap break-words">{lockBox.directions}</p>
                ) : (
                  <p className="text-[13px] text-slate-400">No directions available for this order.</p>
                )}
              </div>
            </div>
          </section>

          <section className="mt-8 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className={sectionTitleCls}>Property Additional Fields</h2>
              {propertyFields.length > 0 && (
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-300">{propertyFields.length} items</span>
              )}
            </div>
            {propertyFields.length === 0 ? (
              <LockboxEmptyState label="property details" />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {propertyFields.map((field, index) => (
                  <div
                    key={`${field?.label || 'field'}-${index}`}
                    className="min-w-0 rounded-3xl border border-slate-100 bg-gradient-to-br from-white via-white to-slate-50/80 p-4 text-sm text-slate-700 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.12),0_10px_30px_rgba(15,23,42,0.08)]"
                  >
                    <p className="break-words text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{field?.label || 'Field'}</p>
                    <div className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-relaxed">{renderDetailValue(field?.value)}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="mt-8 space-y-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <h2 className={sectionTitleCls}>Reservation Details</h2>
              {dateRange && (
                <span className="break-words text-xs text-slate-400">{dateRange}</span>
              )}
            </div>

            {primaryReservation ? (
              <div className="rounded-3xl border border-white/40 bg-white/80 p-5 shadow-[0_18px_48px_rgba(15,23,42,0.1)]">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0">
                    <p className="break-words text-base font-semibold text-slate-900">{primaryReservation?.guestName || 'Primary Guest'}</p>
                    {primaryReservation?.guestEmail && (
                      <p className="break-words text-sm text-slate-500">{primaryReservation.guestEmail}</p>
                    )}
                  </div>
                  {primaryReservation?.phone && (
                    <p className="break-words text-sm font-medium text-slate-600">{primaryReservation.phone}</p>
                  )}
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {Array.isArray(primaryReservation?.additionalFields) && primaryReservation.additionalFields.length > 0 ? (
                    primaryReservation.additionalFields.map((item, index) => (
                      <div
                        key={`${item?.label || 'primary'}-${index}`}
                        className="min-w-0 rounded-2xl border border-slate-100/70 bg-slate-50/70 p-3 text-[13px] text-slate-600"
                      >
                        <p className="break-words text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{item?.label || 'Detail'}</p>
                        <div className="mt-1 whitespace-pre-wrap break-words">{renderDetailValue(item?.value)}</div>
                      </div>
                    ))
                  ) : (
                    <div className="md:col-span-2">
                      <LockboxEmptyState label="additional reservation details" />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <LockboxEmptyState label="primary reservation" />
            )}

            <div className="space-y-5">
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.16em] text-slate-500">Nearby Reservations</h3>
              {neighborGroups.length === 0 ? (
                <LockboxEmptyState label="nearby reservations" />
              ) : (
                neighborGroups.map((group) => (
                  <div key={group.key} className="space-y-3">
                    <p className="break-words text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{group.title}</p>
                    <div className="grid gap-3 md:grid-cols-2">
                      {group.reservations.map((reservation, index) => {
                        const detailEntries = Object.entries(reservation || {})
                          .filter(([key, value]) =>
                            !['guestName', 'guestEmail', 'phone', 'type', 'position', 'date', 'additionalFields', 'id'].includes(key)
                            && value != null
                            && (typeof value !== 'function'));

                        return (
                          <div
                            key={`${group.key}-${reservation?.id || reservation?.guestName || index}`}
                            className="min-w-0 rounded-3xl border border-white/50 bg-white/75 p-4 text-sm text-slate-600 shadow-[0_12px_32px_rgba(15,23,42,0.08)]"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <span className="min-w-0 break-words text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                                {reservation?.type || reservation?.position || group.title}
                              </span>
                              {reservation?.date && (
                                <span className="break-words text-right text-xs text-slate-400">{reservation.date}</span>
                              )}
                            </div>
                            <p className="mt-2 break-words font-semibold text-slate-900">{reservation?.guestName || 'Guest'}</p>
                            {reservation?.guestEmail && (
                              <p className="break-words text-xs text-slate-500">{reservation.guestEmail}</p>
                            )}
                            {reservation?.phone && (
                              <p className="break-words text-xs text-slate-500">{reservation.phone}</p>
                            )}

                            {detailEntries.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {detailEntries.map(([label, value]) => (
                                  <div key={label} className="min-w-0 rounded-xl bg-slate-50/80 px-3 py-2 text-[12px] text-slate-600">
                                    <p className={detailLabelCls}>{humanizeKey(label)}</p>
                                    <div className="mt-1 whitespace-pre-wrap break-words text-[12px] text-slate-600">
                                      {renderDetailValue(value)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {Array.isArray(reservation?.additionalFields) && reservation.additionalFields.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {reservation.additionalFields.map((item, itemIndex) => (
                                  <div key={`${item?.label || 'neighbor-field'}-${itemIndex}`} className="min-w-0 rounded-xl bg-slate-50/80 px-3 py-2 text-[12px] text-slate-600">
                                    <p className={detailLabelCls}>{item?.label || 'Detail'}</p>
                                    <div className="mt-1 whitespace-pre-wrap break-words">{renderDetailValue(item?.value)}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
